import type {
  AdminSetting,
  AdminSettingValue,
} from "@shopexperts/legacy-api";

export type AdminSettingDraftValue = string | boolean;

export type ParsedAdminSettingDraft =
  | { value: AdminSettingValue; error: null }
  | { value: null; error: string };

export function createAdminSettingDraft(setting: AdminSetting): AdminSettingDraftValue {
  if (setting.type === "boolean") return setting.value;
  if (setting.type === "array" || setting.type === "object") {
    return JSON.stringify(setting.value, null, 2);
  }
  return String(setting.value);
}

export function parseAdminSettingDraft(
  setting: AdminSetting,
  draft: AdminSettingDraftValue,
): ParsedAdminSettingDraft {
  if (setting.type === "boolean") {
    return typeof draft === "boolean"
      ? { value: draft, error: null }
      : { value: null, error: "Choose on or off." };
  }

  if (typeof draft !== "string") {
    return { value: null, error: "Enter a valid value." };
  }

  if (setting.type === "string" || setting.type === "text") {
    return { value: draft, error: null };
  }

  if (setting.type === "integer" || setting.type === "float") {
    if (draft.trim() === "") {
      return { value: null, error: "Enter a number." };
    }

    const value = Number(draft);
    if (!Number.isFinite(value)) {
      return { value: null, error: "Enter a valid number." };
    }
    if (setting.type === "integer" && !Number.isInteger(value)) {
      return { value: null, error: "Enter a whole number." };
    }
    return { value, error: null };
  }

  try {
    const value = JSON.parse(draft) as unknown;
    if (setting.type === "array") {
      return Array.isArray(value)
        ? { value, error: null }
        : { value: null, error: "Enter a JSON list, for example [\"one\", \"two\"]." };
    }
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      return { value: null, error: "Enter a JSON object, for example {\"key\": \"value\"}." };
    }
    return { value: value as Record<string, unknown>, error: null };
  } catch {
    return { value: null, error: "Enter valid JSON." };
  }
}

export function isAdminSettingChanged(
  setting: AdminSetting,
  draft: AdminSettingDraftValue,
): boolean {
  const parsed = parseAdminSettingDraft(setting, draft);
  return parsed.error === null && JSON.stringify(parsed.value) !== JSON.stringify(setting.value);
}

export function formatAdminSettingGroup(group: string): string {
  return group
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
